import { NgClass } from '@angular/common'
import { Component, Inject, OnInit } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog'
import { AutoCompleteModule } from 'primeng/autocomplete'
import { PrimeNG } from 'primeng/config'
import { FileUpload } from 'primeng/fileupload'
import { SpinnerOverlayService } from '../../../Components/spinner/spinner-overlay.service'
import { MessageService } from '../../../shared/message.service'
import { IColor } from '../color-list/color.model'
import { ColorService } from '../color-list/color.service'
import { Button } from "../../../Components/button/button";

@Component({
  selector: 'app-color-update',
  imports: [
    MatDialogContent,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    AutoCompleteModule,
    FileUpload,
    Button
  ],
  templateUrl: './color-update.component.html',
  styleUrl: './color-update.component.css'
})
export class ColorUpdateComponent implements OnInit {
  form!: FormGroup
  color: IColor | null = null
  files = [];
  totalSize: number = 0
  totalSizePercent: number = 0
  selectedImages: File[] = []

  constructor(
    private config: PrimeNG, private spinner: SpinnerOverlayService,
    private fb: FormBuilder, public dialogRef: MatDialogRef<ColorUpdateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private colorService: ColorService,
    private messageService: MessageService
  ) {
  }

  ngOnInit() {
    this.form = this.fb.group({
      name: [{value: this.color?.name ?? '', disabled: true}, [Validators.required]],
      images: [this.color?.url ?? [], [Validators.required]],
    })

    if (this.data) {
      this.findById()
    }
  }

  findById() {
    this.handleLoading(true)

    this.colorService.findById(this.data.id).subscribe({
      next: (response) => {
        this.color = response.body?.color ?? null
        this.patchFormProduct()
        this.handleLoading(false)
      },
      error: () => {
        this.handleLoading(false)
      }
    })
  }

  close(save: boolean): void {
    this.dialogRef.close(save)
  }

  get f() {
    return this.form.controls
  }

  get colorArray(): FormArray {
    return this.form.get('color') as FormArray;
  }

  addProductImages(event: any) {
    const files: File[] = Array.from(event.files);

    const control = this.form.get('images');

    const updatedFiles = [
      files[0]
    ];
    console.log(updatedFiles);

    this.form.patchValue({
      images: updatedFiles
    })
    control?.markAsDirty();
    control?.updateValueAndValidity();
  }


  addColorImage(event: any, index: number) {
    const file: File = event.files[0];
    if (!file) return;
    const colorArray = this.form.get('color') as FormArray;
    const colorGroup = colorArray.at(index) as FormGroup;

    colorGroup.patchValue({ image: file });
    colorGroup.get('image')?.markAsDirty();
    colorGroup.get('image')?.markAsTouched();
    colorGroup.updateValueAndValidity();
  }

  removeColorImage(index: number) {
    const colorArray = this.form.get('color') as FormArray;
    const colorGroup = colorArray.at(index) as FormGroup;

    colorGroup.patchValue({ image: null });
    colorGroup.get('image')?.markAsDirty();
    colorGroup.get('image')?.markAsTouched();
    colorGroup.get('image')?.updateValueAndValidity();
  }

  removeAllProductImages() {
    this.form.patchValue({
      images: []
    })
  }

  onRemoveTemplatingFile(event: any, file: {
    size: any
  }, removeFileCallback: (arg0: any, arg1: any) => void, index: any) {
    removeFileCallback(event, index);
    this.totalSize -= parseInt(this.formatSize(file.size));
    this.totalSizePercent = this.totalSize / 10;
    const control = this.form.get('images');
    if (!control) return;

    const updatedFiles = [...(control.value || [])];
    updatedFiles.splice(index, 1);

    control.patchValue(updatedFiles);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  formatSize(bytes: number) {
    const k = 1024;
    const dm = 3;
    const sizes = this.config.translation.fileSizeTypes;
    if (bytes === 0) {
      return `0 ${sizes ? sizes : [0]}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${sizes ? sizes : [i]}`;
  }

  update() {
    this.handleLoading(true);

    const formValue = this.form.value;

    if (formValue.images.length > 5) {
      this.messageService.showError("Se excedió la cantidad de imágenes permitidas para el producto (5)")
      this.handleLoading(false)
      return
    }

    const formData = new FormData();

    try {

      formData.append('id', String(this.color?.id ?? ''));
      formData.append('active', String(this.color?.active ?? false));

      formValue.images.forEach((file: File, i: number) => {
        const hasOriginalUrl = (file as any).originalUrl;

        if (hasOriginalUrl) {
          formData.append("existingImage", (file as any).originalUrl);
        } else {
          formData.append("images", file);
        }
      });
    }
    catch (e) {
      this.messageService.showError("Error al preparar las imágenes del producto: " + e);
      this.handleLoading(false);
      return;
    }

    this.colorService.update(formData, this.data.id).subscribe({
      next: () => {
        this.handleLoading(false);
        this.close(true);
      },
      error: () => {
        this.handleLoading(false);
      }
    });
  }

  async patchFormProduct() {
    const files: File[] = [];
    const existingImage = this.color?.url

    if (existingImage && typeof existingImage === 'string') {
      try {
        const res = await fetch(existingImage);
        const blob = await res.blob();
        const fileName = existingImage.split('/').pop() || `image-${files.length + 1}.jpg`;
        const file = new File([blob], fileName, { type: blob.type });
        (file as any).originalUrl = existingImage;
        files.push(file);
      } catch (e) {
        this.messageService.showError(`Error al cargar la imagen ${existingImage}: ` + e);
      }
    }

    if (!this.color) return;

    this.form.patchValue({
      name: this.color?.name,
      images: files
    });

    this.selectedImages = files;
  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }

  choose(event: any, callback: any) {
    callback();
  }
}
